// Загрузка данных досье по блокам (RD-149). Каждый блок — свой ключ (ТУ/User + период), поэтому
// запоздалый ответ прежнего ТУ/периода не попадает в новый блок. Смена User (epoch) отбрасывает всё.
// Запросы выполняются только при наличии права: общий http-клиент на 403 выполняет logOut.
// r3: дисциплина, биржа и кадровые документы — операции RD-161 по User UUID; ошибки не заменяются mock.
import { computed, reactive, ref } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import {
    getEmploymentSchedule,
    getUser,
    getUserDayStateDocuments,
    getUserDiscipline,
    getUserExchangeSummary,
    getUserVacancyResponses,
} from '@/api/users'
import { getStats } from '@/api/schedule'
import { useAuthStore } from '@/stores/authStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import {
    type Block,
    type DossierEmployment,
    type DossierPerson,
    type ScheduleBody,
    DATE_KEY,
    mergeEmployment,
    normalizePerson,
    normalizeScheduleBody,
    normalizeUserEmployments,
} from './dossierModel'
import {
    type DayStateDocument,
    type DisciplinePage,
    type ExchangeRange,
    type ExchangeResponse,
    type ExchangeSummary,
    type Paged,
    normalizeDisciplinePage,
    normalizeDocuments,
    normalizeExchangeSummary,
    normalizeResponsesPage,
} from './dossierActivity'

export interface DossierPermissions {
    viewUser: boolean
    viewStats: boolean
    viewExchange: boolean
    editUser: boolean
    changeSmart: boolean
    editEmployment: boolean
    createEmployment: boolean
    resetPassword: boolean
}

export function useDossierPermissions() {
    const authStore = useAuthStore()
    return computed<DossierPermissions>(() => {
        const p = authStore.permissions
        return {
            // GET /api/user/{id}/ и /api/employments/{id}/schedule/ — server can_view_user_list.
            // Также RD-161 discipline и day-state-documents.
            viewUser: Boolean(p?.can_view_user_list),
            // GET /api/shifts/employee-statistics/ — can_view_schedule_and_timesheet.
            viewStats: Boolean(p?.can_view_schedule_and_timesheet),
            // RD-161 exchange-summary и vacancy-responses.
            viewExchange: Boolean(p?.can_view_vacancy_responses),
            editUser: Boolean(p?.can_edit_user_main_info),
            changeSmart: Boolean(p?.can_change_smart_checkin_possible),
            editEmployment: Boolean(p?.can_edit_employment),
            createEmployment: Boolean(p?.can_create_employment),
            // UI can_reset_user_password; фактический server gate — can_view_user_list (известный gap),
            // без него запрос завершился бы 403 → logOut, поэтому подэкран требует оба права.
            resetPassword: Boolean(p?.can_reset_user_password && p?.can_view_user_list),
        }
    })
}

const monthKey = (date: Dayjs) => date.format('YYYY-MM')

export const DISCIPLINE_PAGE_SIZE = 10
export const RESPONSES_PAGE_SIZE = 10

export interface DisciplineQuery {
    from: string // YYYY-MM
    to: string
    page: number
}

export interface ResponsesQuery {
    range: ExchangeRange
    page: number
}

const rangeKey = (range: ExchangeRange) => `${range.date_from ?? ''}|${range.date_to}`

interface ScheduleEntry extends Block<ScheduleBody> {
    employmentId: string
    start: string
    end: string
}

export function useDossierData(initial: { person: DossierPerson, employments: DossierEmployment[] }) {
    const permissions = useDossierPermissions()
    const departmentStore = useDepartmentStore()
    const scheduleStore = useScheduleStore()
    const directories = () => ({ departments: departmentStore.departments, positions: scheduleStore.positions })

    const person = ref<DossierPerson>(initial.person)
    const employments = ref<DossierEmployment[]>(initial.employments)
    // Данные User: известная запись (строка списка/графика) показывается сразу, GET актуализирует.
    const userStatus = ref<'loading' | 'ready' | 'error' | 'forbidden'>(permissions.value.viewUser ? 'loading' : 'forbidden')
    let epoch = 0
    let userRequest = 0

    const schedules = reactive(new Map<string, ScheduleEntry>())
    const stats = reactive(new Map<string, Block<unknown>>())

    function applyUser(body: Record<string, unknown>) {
        person.value = normalizePerson(body)
        const known = new Map(employments.value.map(item => [item.id, item]))
        employments.value = normalizeUserEmployments(body.employments, directories(), true)
            .map(item => mergeEmployment(known.get(item.id), item))
    }

    /** GET /api/user/{User UUID}/. Возвращает true при успехе; ошибка остаётся в userStatus. */
    async function reloadUser(): Promise<boolean> {
        if (!permissions.value.viewUser) {
            userStatus.value = 'forbidden'
            return false
        }
        const request = ++userRequest
        const requestEpoch = epoch
        const userId = person.value.id
        if (!userId) {
            userStatus.value = 'error'
            return false
        }
        userStatus.value = 'loading'
        try {
            const res = await getUser(userId)
            if (request !== userRequest || requestEpoch !== epoch) return false
            applyUser(res.data.body ?? {})
            userStatus.value = 'ready'
            return true
        } catch {
            if (request === userRequest && requestEpoch === epoch) userStatus.value = 'error'
            return false
        }
    }

    // ---------- Смены ----------

    const scheduleKey = (employmentId: string, start: string, end: string) => `${employmentId}|${start}|${end}`

    function findCoverage(employmentId: string, start: string, end: string) {
        for (const entry of schedules.values()) {
            if (entry.employmentId === employmentId && entry.status === 'ready' && entry.start <= start && entry.end >= end) return entry
        }
        return null
    }

    const LOADING = { status: 'loading', data: null } as const
    const FORBIDDEN = { status: 'forbidden', data: null } as const

    // Кэш блоков по ключу: чтение без побочных эффектов, ensure — запуск при отсутствии, retry — повтор.
    // Ответ после смены epoch (инвалидация) или замены записи отбрасывается.
    function blockCache<P, T>(allowed: () => boolean, keyOf: (params: P) => string, fetch: (userId: string, params: P) => Promise<T>) {
        const entries = reactive(new Map()) as Map<string, Block<T>>
        async function load(params: P) {
            const key = keyOf(params)
            const entry = entries.get(key)
            const userId = person.value.id
            if (!entry) return
            if (!userId) {
                entry.status = 'error'
                return
            }
            const requestEpoch = epoch
            entry.status = 'loading'
            try {
                const data = await fetch(userId, params)
                if (requestEpoch !== epoch || entries.get(key) !== entry) return
                entry.data = data
                entry.status = 'ready'
            } catch {
                if (requestEpoch === epoch && entries.get(key) === entry) entry.status = 'error'
            }
        }
        return {
            read(params: P): Block<T> {
                if (!allowed()) return FORBIDDEN
                return entries.get(keyOf(params)) ?? LOADING
            },
            ensure(params: P) {
                if (!allowed()) return
                const key = keyOf(params)
                if (entries.has(key)) return
                entries.set(key, { status: 'loading', data: null })
                void load(params)
            },
            retry(params: P) {
                if (allowed() && entries.has(keyOf(params))) void load(params)
            },
            clear: () => entries.clear(),
        }
    }

    /** Чтение блока смен без побочных эффектов: загруженное покрытие диапазона переиспользуется. */
    function readSchedule(employmentId: string, start: string, end: string): Block<ScheduleBody> {
        if (!permissions.value.viewUser) return { status: 'forbidden', data: null }
        return findCoverage(employmentId, start, end) ?? schedules.get(scheduleKey(employmentId, start, end)) ?? LOADING
    }

    /** GET /api/employments/{Employment UUID}/schedule/?start_date&end_date — запуск, если диапазон не покрыт. */
    function ensureSchedule(employmentId: string, start: string, end: string) {
        if (!permissions.value.viewUser || findCoverage(employmentId, start, end)) return
        const key = scheduleKey(employmentId, start, end)
        if (schedules.has(key)) return
        schedules.set(key, { employmentId, start, end, status: 'loading', data: null })
        void loadSchedule(key)
    }

    async function loadSchedule(key: string) {
        const entry = schedules.get(key)
        if (!entry) return
        if (!permissions.value.viewUser) {
            entry.status = 'forbidden'
            return
        }
        const requestEpoch = epoch
        entry.status = 'loading'
        try {
            const res = await getEmploymentSchedule(entry.employmentId, { start_date: entry.start, end_date: entry.end })
            if (requestEpoch !== epoch || schedules.get(key) !== entry) return
            entry.data = normalizeScheduleBody(res.data.body)
            entry.status = 'ready'
        } catch {
            if (requestEpoch === epoch && schedules.get(key) === entry) entry.status = 'error'
        }
    }

    function retrySchedule(employmentId: string, start: string, end: string) {
        const key = scheduleKey(employmentId, start, end)
        if (schedules.has(key)) void loadSchedule(key)
    }

    // ---------- Метрики подразделения за месяц ----------

    const statsKey = (departmentId: string, month: Dayjs) => `${departmentId}|${monthKey(month)}`

    /** GET /api/shifts/employee-statistics/?department&month&year — значения выбираются по employment_id. */
    function readStats(departmentId: string | null, month: Dayjs): Block<unknown> {
        if (!permissions.value.viewStats) return { status: 'forbidden', data: null }
        if (!departmentId) return { status: 'error', data: null }
        return stats.get(statsKey(departmentId, month)) ?? LOADING
    }

    function ensureStats(departmentId: string | null, month: Dayjs) {
        if (!permissions.value.viewStats || !departmentId) return
        const key = statsKey(departmentId, month)
        if (stats.has(key)) return
        stats.set(key, { status: 'loading', data: null })
        void loadStats(departmentId, month)
    }

    async function loadStats(departmentId: string, month: Dayjs) {
        const key = statsKey(departmentId, month)
        const entry = stats.get(key)
        if (!entry) return
        const requestEpoch = epoch
        entry.status = 'loading'
        try {
            const res = await getStats({ department: departmentId, month: month.month() + 1, year: month.year() })
            if (requestEpoch !== epoch || stats.get(key) !== entry) return
            entry.data = res.data.body ?? null
            entry.status = 'ready'
        } catch {
            if (requestEpoch === epoch && stats.get(key) === entry) entry.status = 'error'
        }
    }

    // ---------- RD-161 ----------

    // GET /api/user/{User UUID}/discipline/?month_from&month_to&page&page_size — все доступные ТУ User.
    const discipline = blockCache<DisciplineQuery, DisciplinePage>(
        () => permissions.value.viewUser,
        query => `${query.from}|${query.to}|${query.page}`,
        async (userId, query) => {
            const res = await getUserDiscipline(userId, { month_from: query.from, month_to: query.to, page: query.page, page_size: DISCIPLINE_PAGE_SIZE })
            return normalizeDisciplinePage(res.data.body)
        },
    )

    // GET /api/user/{User UUID}/exchange-summary/?date_from&date_to — все подразделения User.
    const exchangeSummary = blockCache<ExchangeRange, ExchangeSummary>(
        () => permissions.value.viewExchange,
        rangeKey,
        async (userId, range) => normalizeExchangeSummary((await getUserExchangeSummary(userId, range)).data.body),
    )

    // GET /api/user/{User UUID}/vacancy-responses/?date_from&date_to&page&page_size.
    const responses = blockCache<ResponsesQuery, Paged<ExchangeResponse>>(
        () => permissions.value.viewExchange,
        query => `${rangeKey(query.range)}|${query.page}`,
        async (userId, query) => {
            const res = await getUserVacancyResponses(userId, { ...query.range, page: query.page, page_size: RESPONSES_PAGE_SIZE })
            return normalizeResponsesPage(res.data.body)
        },
    )

    // GET /api/user/{User UUID}/day-state-documents/ — незавершённые/будущие документы ТУ в scope.
    const documents = blockCache<void, DayStateDocument[]>(
        () => permissions.value.viewUser,
        () => 'all',
        async (userId) => {
            const list = normalizeDocuments((await getUserDayStateDocuments(userId)).data.body)
            if (!list) throw new Error('Некорректный ответ day-state-documents')
            return list
        },
    )

    function retryStats(departmentId: string | null, month: Dayjs) {
        if (departmentId) void loadStats(departmentId, month)
    }

    /** После подтверждённой записи: данные периода могли измениться — кэш сбрасывается. */
    function invalidatePeriodData() {
        epoch++
        schedules.clear()
        stats.clear()
        discipline.clear()
        exchangeSummary.clear()
        responses.clear()
        documents.clear()
    }

    return {
        permissions,
        person,
        employments,
        userStatus,
        reloadUser,
        readSchedule,
        ensureSchedule,
        retrySchedule,
        readStats,
        ensureStats,
        retryStats,
        discipline,
        exchangeSummary,
        responses,
        documents,
        invalidatePeriodData,
    }
}

export type DossierData = ReturnType<typeof useDossierData>

// Месяцы периода [from..to] включительно.
export function monthsBetween(from: Dayjs, to: Dayjs) {
    const result: Dayjs[] = []
    let cursor = from.startOf('month')
    while (!cursor.isAfter(to, 'month')) {
        result.push(cursor)
        cursor = cursor.add(1, 'month')
    }
    return result
}

export const rangeOfMonth = (month: Dayjs) => ({
    start: month.startOf('month').format(DATE_KEY),
    end: month.endOf('month').format(DATE_KEY),
})

export const toDay = (value: string) => dayjs(value, DATE_KEY)
